import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { chatTitle, type ChatSummary } from '@/entities/chats';
import { formatListTime as formatWhen, crmName, labelOf, useI18n } from '@/shared/i18n';
import { cn } from '@/shared/lib/utils';
import { Avatar, AvatarFallback } from '@/shared/ui/shadcn/avatar';
import { Input } from '@/shared/ui/shadcn/input';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';

function initials(title: string) {
  const parts = title.split(/\s+/).filter(Boolean);
  const letters = (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
  return (letters || title.slice(0, 2) || '?').toUpperCase();
}

export function ClientChatList({ chats, activeId }: { chats: ChatSummary[]; activeId?: string }) {
  const { t, locale } = useI18n();
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return chats;
    return chats.filter((chat) => {
      const haystack = [chatTitle(chat), chat.phone, chat.channel, chat.stage, chat.last_message]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [chats, query]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col border-r bg-background">
      <div className="shrink-0 border-b p-3">
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t.chats.search}
          aria-label={t.chats.search}
        />
      </div>
      <ScrollArea className="min-h-0 flex-1">
        {filtered.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            {chats.length === 0 ? t.chats.empty : t.chats.noMatches}
          </p>
        ) : (
          filtered.map((chat) => {
            const title = chatTitle(chat) || t.common.lead;
            const subtitle = [labelOf(t.channels, chat.channel), crmName(t.crm.names, chat.stage)]
              .filter(Boolean)
              .join(' · ');
            return (
              <NavLink
                key={chat.dialog_id}
                to={`/chats/${chat.dialog_id}`}
                className={cn(
                  'flex gap-3 border-b px-3 py-3 hover:bg-muted/60',
                  activeId === chat.dialog_id && 'bg-muted',
                )}
              >
                <Avatar className="size-9">
                  <AvatarFallback className="text-[10px]">{initials(title)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium">{title}</span>
                    <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">
                      {formatWhen(chat.last_activity_at, locale)}
                    </span>
                  </div>
                  {subtitle ? (
                    <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                  ) : null}
                  <p className="truncate text-xs text-muted-foreground">
                    {chat.last_message || t.chats.noMessages}
                  </p>
                </div>
              </NavLink>
            );
          })
        )}
      </ScrollArea>
    </div>
  );
}
